import { z } from 'zod';
import { fieldAssessmentAdviceSchema } from './field-assessment.js';
import { fieldWorkspaceContextSchema } from './field-workspace.js';
import { fieldReadinessReportSchema, fieldSnapshotSchema, fieldModerationSchema } from './field.js';
export const fieldReportEntrySchema = z
    .object({ digest: z.string().regex(/^[a-f0-9]{64}$/), report: fieldReadinessReportSchema })
    .strict();
export const fieldAppealSchema = z
    .object({
    id: z.uuid(),
    engagementId: z.uuid(),
    reportDigest: z.string(),
    candidateRevision: z.string(),
    reason: z.string(),
    evidenceIds: z.array(z.string()),
    status: z.enum(['submitted', 'under_review', 'resolved']),
    reviewerId: z.uuid().nullable(),
    response: z.string().nullable(),
    replacementReportDigest: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const fieldAppealCreateSchema = z
    .object({
    requestId: z.uuid(),
    reportDigest: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().min(40).max(8000),
    evidenceIds: z.array(z.string().min(1).max(200)).min(1).max(20),
})
    .strict();
export const fieldReportHistorySchema = z
    .object({ reports: z.array(fieldReportEntrySchema), appeals: z.array(fieldAppealSchema) })
    .strict();
export const fieldAdminEngagementSchema = z
    .object({
    learnerId: z.uuid(),
    engagementId: z.uuid(),
    title: z.string(),
    day: z.number().int(),
    revision: z.number().int(),
    status: z.string(),
    assessmentStatus: z.string(),
    updatedAt: z.iso.datetime(),
    latestScore: z.number().nullable(),
    pendingAppeals: z.number().int(),
})
    .strict();
export const fieldAdminListSchema = z
    .object({ engagements: z.array(fieldAdminEngagementSchema), nextCursor: z.uuid().nullable() })
    .strict();
export const fieldAdminDetailSchema = fieldReportHistorySchema
    .extend({
    learnerId: z.uuid(),
    snapshot: fieldSnapshotSchema,
    advice: fieldAssessmentAdviceSchema.optional(),
})
    .strict();
export const fieldAppealReviewSchema = z
    .object({ requestId: z.uuid(), expectedRevision: z.number().int().nonnegative() })
    .strict();
export const fieldAppealResolveSchema = z
    .object({
    requestId: z.uuid(),
    response: z.string().min(40).max(8000),
    replacementReportDigest: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const fieldAdminModerationSchema = fieldModerationSchema;
const revision = z.string().regex(/^[a-f0-9]{64}$/);
export const FIELD_ADMIN_SOURCE_PAGE_CHARS = 8000;
export const fieldAdminSourceQuerySchema = z
    .object({
    path: z.string().min(1).max(300),
    page: z.coerce.number().int().min(0).max(1000).default(0),
})
    .strict();
const sourceIdentity = z.object({
    engagementId: z.uuid(),
    prId: z.uuid(),
    revision,
    currentRevision: revision,
});
export const fieldAdminRevisionListSchema = z
    .object({
    currentRevision: revision,
    revisions: z.array(z.object({ revision, fileCount: z.number().int().nonnegative() })).max(50),
    nextOffset: z.number().int().nonnegative().nullable(),
})
    .strict();
export const fieldAdminSourceManifestSchema = sourceIdentity
    .extend({
    provenance: z.enum(['learner_upload', 'workspace_capture', 'unknown']),
    capture: z
        .object({
        context: fieldWorkspaceContextSchema,
        operationId: z.uuid(),
        artifactHash: revision,
        capturedAt: z.string(),
    })
        .optional(),
    files: z
        .array(z.object({
        path: z.string(),
        byteSize: z.number().int().nonnegative().max(200000),
        sha256: revision,
        pageCount: z.number().int().positive(),
    }))
        .max(120),
    totalBytes: z.number().int().nonnegative().max(1200000),
    interpretation: z.literal('source_snapshot_only'),
})
    .strict();
export const fieldAdminSourcePageSchema = sourceIdentity
    .extend({
    path: z.string(),
    sha256: revision,
    byteSize: z.number().int().nonnegative().max(200000),
    page: z.number().int().nonnegative(),
    pageCount: z.number().int().positive(),
    content: z.string().max(FIELD_ADMIN_SOURCE_PAGE_CHARS * 2),
    interpretation: z.literal('source_snapshot_only'),
})
    .strict();
export const fieldAdminDocumentQuerySchema = z
    .object({
    version: z.coerce.number().int().positive(),
    format: z.enum(['markdown', 'html', 'json', 'yaml', 'csv', 'pdf']),
})
    .strict();
